<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const agents = useMapGetter('agents/getAgents');

const search = ref(route.query.q?.toString() || '');
const filter = ref(route.query.filter?.toString() || '');
const stateFilter = ref(route.query.state?.toString() || '');
const sort = ref(route.query.sort?.toString() || 'due_at_asc');

const tickets = computed(() => Object.values(store.state.tickets?.records || {}));
const counts = computed(() => store.getters['tickets/getCounts'] || {});
const isFetching = computed(() => store.getters['tickets/getUIFlags']?.isFetching);

const filters = computed(() => [
  { key: '', labelKey: 'TICKETING.LIST.FILTERS.ALL', count: counts.value.open },
  { key: 'mine', labelKey: 'TICKETING.LIST.FILTERS.MINE', count: counts.value.mine },
  {
    key: 'unassigned',
    labelKey: 'TICKETING.LIST.FILTERS.UNASSIGNED',
    count: counts.value.unassigned,
  },
  {
    key: 'overdue',
    labelKey: 'TICKETING.LIST.FILTERS.OVERDUE',
    count: counts.value.overdue,
  },
  { key: 'due_today', labelKey: 'TICKETING.LIST.FILTERS.DUE_TODAY' },
  { key: 'due_this_week', labelKey: 'TICKETING.LIST.FILTERS.DUE_THIS_WEEK' },
  { key: 'no_due_date', labelKey: 'TICKETING.LIST.FILTERS.NO_DUE_DATE' },
]);

const stateOptions = [
  '',
  'ticket_new',
  'open',
  'in_progress',
  'waiting_for_customer',
  'waiting_for_internal_team',
  'resolved',
  'closed',
  'reopened',
];

const CLOSED_STATES = new Set(['resolved', 'closed']);

const daysPastDue = dueAt => {
  if (!dueAt) return 0;
  const due = new Date(dueAt);
  const now = new Date();
  if (due >= now) return 0;
  return Math.max(1, Math.ceil((now - due) / (1000 * 60 * 60 * 24)));
};

const overdueLevel = ticket => {
  if (!ticket.due_at || CLOSED_STATES.has(ticket.state)) return null;
  const days = daysPastDue(ticket.due_at);
  if (days <= 0) return null;
  if (days >= 3) return 'critical';
  return 'warning';
};

const agentName = ticket => {
  const fromApi =
    ticket.conversation?.assignee?.available_name ||
    ticket.conversation?.assignee?.name;
  if (fromApi) return fromApi;
  const id = ticket.conversation?.assignee_id;
  if (!id) return null;
  const agent = (agents.value || []).find(a => Number(a.id) === Number(id));
  return agent?.available_name || agent?.name || null;
};

const contactName = ticket =>
  ticket.conversation?.contact?.name || t('TICKETING.LIST.UNKNOWN_CONTACT');

const contactEmail = ticket => ticket.conversation?.contact?.email || '';

const formatDue = dueAt => {
  if (!dueAt) return t('TICKETING.LIST.NO_DUE');
  try {
    return new Date(dueAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dueAt;
  }
};

const dueLabel = ticket => {
  const level = overdueLevel(ticket);
  if (!level) return formatDue(ticket.due_at);
  const days = daysPastDue(ticket.due_at);
  return t('TICKETING.LIST.OVERDUE_BY_DAYS', { days, date: formatDue(ticket.due_at) });
};

const rowClass = ticket => {
  const level = overdueLevel(ticket);
  if (level === 'critical') {
    return 'border-s-4 border-red-500 bg-red-50/70 dark:bg-red-950/30';
  }
  if (level === 'warning') {
    return 'border-s-4 border-orange-500 bg-orange-50/70 dark:bg-orange-950/25';
  }
  return 'border-s-4 border-transparent';
};

const load = () => {
  const params = {
    q: search.value || undefined,
    filter: filter.value || undefined,
    state: stateFilter.value || undefined,
    sort: sort.value || undefined,
  };
  store.dispatch('tickets/fetchTickets', params);
  store.dispatch('tickets/fetchCounts');
  router.replace({ query: params });
};

onMounted(() => {
  if (
    isFeatureEnabled.value(
      route.params.accountId,
      FEATURE_FLAGS.TICKET_MANAGEMENT
    )
  ) {
    store.dispatch('agents/get');
    load();
  }
});

watch([filter, sort, stateFilter], load);

const openConversation = ticket => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: ticket.conversation.id,
    },
  });
};

const emailCustomer = (ticket, event) => {
  event.stopPropagation();
  const email = contactEmail(ticket);
  if (!email) return;

  const subject = `${ticket.ticket_number} — ${ticket.title || ''}`.trim();
  const detail = { email, subject, ticketNumber: ticket.ticket_number };

  window.dispatchEvent(new CustomEvent('sina:compose-email', { detail }));

  // Fallback if dashboard script is unavailable
  setTimeout(() => {
    if (document.documentElement.getAttribute('data-sina-compose-active') === '1') return;
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}`;
  }, 900);
};
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-n-background">
    <header class="flex flex-col gap-3 px-4 py-3 border-b border-n-weak">
      <div class="flex items-center justify-between gap-3">
        <h1 class="text-base font-medium text-n-slate-12">
          {{ $t('TICKETING.LIST.TITLE') }}
        </h1>
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <span v-if="counts.overdue" class="text-orange-600 dark:text-orange-400 font-medium">
            {{ counts.overdue }} {{ $t('TICKETING.LIST.FILTERS.OVERDUE') }}
          </span>
          <span v-if="counts.open != null">
            {{ counts.open }} {{ $t('TICKETING.LIST.FILTERS.OPEN') }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          v-model="search"
          class="woot-input flex-1"
          type="search"
          :placeholder="$t('TICKETING.LIST.SEARCH_PLACEHOLDER')"
          @keyup.enter="load"
        />
        <div class="flex gap-2 shrink-0">
          <select v-model="stateFilter" class="woot-input text-xs min-w-[9rem]">
            <option value="">{{ $t('TICKETING.LIST.STATE_ALL') }}</option>
            <option
              v-for="state in stateOptions.filter(Boolean)"
              :key="state"
              :value="state"
            >
              {{ $t(`TICKETING.STATES.${state.toUpperCase()}`) }}
            </option>
          </select>
          <select v-model="sort" class="woot-input text-xs min-w-[10rem]">
            <option value="due_at_asc">{{ $t('TICKETING.LIST.SORT.DUE_ASC') }}</option>
            <option value="due_at_desc">{{ $t('TICKETING.LIST.SORT.DUE_DESC') }}</option>
            <option value="updated_at_desc">{{ $t('TICKETING.LIST.SORT.UPDATED_DESC') }}</option>
            <option value="updated_at_asc">{{ $t('TICKETING.LIST.SORT.UPDATED_ASC') }}</option>
            <option value="created_at_desc">{{ $t('TICKETING.LIST.SORT.CREATED_DESC') }}</option>
            <option value="priority_desc">{{ $t('TICKETING.LIST.SORT.PRIORITY_DESC') }}</option>
            <option value="priority_asc">{{ $t('TICKETING.LIST.SORT.PRIORITY_ASC') }}</option>
          </select>
        </div>
      </div>

      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="item in filters"
          :key="item.key || 'all'"
          type="button"
          class="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition-colors"
          :class="
            filter === item.key
              ? 'bg-n-brand/15 border-n-brand text-n-slate-12'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="filter = item.key"
        >
          <span>{{ $t(item.labelKey) }}</span>
          <span
            v-if="item.count != null"
            class="tabular-nums opacity-70"
          >{{ item.count }}</span>
        </button>
      </div>
    </header>

    <div v-if="isFetching" class="p-4 text-n-slate-11 text-sm">
      {{ $t('TICKETING.LIST.LOADING') }}
    </div>
    <div v-else-if="!tickets.length" class="p-6 text-n-slate-11 text-sm">
      {{ $t('TICKETING.LIST.EMPTY') }}
    </div>

    <ul v-else class="overflow-y-auto flex-1 divide-y divide-n-weak">
      <li
        v-for="ticket in tickets"
        :key="ticket.ticket_number"
        class="px-4 py-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
        :class="rowClass(ticket)"
        @click="openConversation(ticket)"
      >
        <div class="flex items-start gap-3">
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span class="font-semibold text-n-slate-12 tabular-nums">
                {{ ticket.ticket_number }}
              </span>
              <span class="text-n-slate-12 truncate">{{ ticket.title }}</span>
              <span
                v-if="overdueLevel(ticket) === 'warning'"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-700 dark:text-orange-300"
              >
                {{ $t('TICKETING.LIST.OVERDUE_BADGE') }}
              </span>
              <span
                v-else-if="overdueLevel(ticket) === 'critical'"
                class="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300"
              >
                {{ $t('TICKETING.LIST.CRITICAL_BADGE') }}
              </span>
            </div>

            <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11">
              <span class="inline-flex items-center gap-1">
                <span class="i-lucide-user size-3.5 opacity-70" />
                <span>{{ agentName(ticket) || $t('TICKETING.LIST.UNASSIGNED_AGENT') }}</span>
              </span>
              <span class="inline-flex items-center gap-1 min-w-0">
                <span class="i-lucide-contact size-3.5 opacity-70 shrink-0" />
                <span class="truncate">{{ contactName(ticket) }}</span>
              </span>
              <span
                class="inline-flex items-center gap-1"
                :class="{
                  'text-orange-700 dark:text-orange-300 font-medium': overdueLevel(ticket) === 'warning',
                  'text-red-700 dark:text-red-300 font-medium': overdueLevel(ticket) === 'critical',
                }"
              >
                <span class="i-lucide-calendar-clock size-3.5 opacity-70" />
                <span>{{ dueLabel(ticket) }}</span>
              </span>
              <span
                v-if="ticket.conversation?.priority"
                class="inline-flex items-center gap-1"
              >
                <span class="i-lucide-flag size-3.5 opacity-70" />
                <span>{{ $t(`TICKETING.PRIORITIES.${ticket.conversation.priority.toUpperCase()}`) }}</span>
              </span>
              <span class="ms-auto text-n-slate-10">
                {{ $t(`TICKETING.STATES.${ticket.state.toUpperCase()}`) }}
              </span>
            </div>
          </div>

          <button
            type="button"
            class="shrink-0 mt-0.5 inline-flex items-center justify-center size-8 rounded-md border border-n-weak text-n-slate-11 hover:bg-n-brand/10 hover:text-n-brand hover:border-n-brand disabled:opacity-40 disabled:pointer-events-none"
            :disabled="!contactEmail(ticket)"
            :title="
              contactEmail(ticket)
                ? $t('TICKETING.LIST.EMAIL_CUSTOMER')
                : $t('TICKETING.LIST.NO_EMAIL')
            "
            @click="emailCustomer(ticket, $event)"
          >
            <span class="i-lucide-mail size-4" />
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>
